import type { Item, Level, Room } from '../core/hp/types';
import { removeRoom } from '../core/geometry/edit';
import { containsPoint } from './sceneGesture';
import { cachedModelTags } from './modelTags';

/** The app's AddLevel.MaxLevels: floors with the basements; a roof floor may stand over them. */
export const MAX_LEVELS = 5;
const MAX_LEVELS_WITH_ROOF = MAX_LEVELS + 1;

type TagsOf = (modelId: string) => readonly string[];
const knownTags: TagsOf = id => cachedModelTags(id) ?? [];

/** FurnitureInfo.IsHouseRoof: a roof over the house, not a skylight or a canopy — only house roofs carry `Floor`. */
function isHouseRoof(item: Item, tagsOf: TagsOf): boolean {
  const tags = item.tags ?? tagsOf(item.modelRef.id);
  return tags.includes('Roofs') && tags.includes('Floor') && !tags.includes('SkylightRoof');
}

/** RoofRooms.IsRoofRoom: most of the room's walls hidden and a house roof on its floor; a terrace has no roof. */
export function isRoofRoom(level: Level, room: Room, tagsOf: TagsOf = knownTags): boolean {
  const walls = level.walls.filter(wall => wall.roomIndex === room.index);
  if (!walls.length || walls.filter(wall => wall.hidden).length * 2 <= walls.length) return false;
  // The app reads the room's own floor furniture; a web item keeps no room, so it is the room it stands in.
  return level.items.some(item => item.placement === 'floor' && containsPoint(room.polygon, item.position) && isHouseRoof(item, tagsOf));
}

/** RoofRooms.IsRoofLevel: every room of the floor is a roof room. Nothing in the project marks it. */
export function isRoofLevel(level: Level | undefined, tagsOf: TagsOf = knownTags): boolean {
  return !!level?.rooms.length && level.rooms.every(room => isRoofRoom(level, room, tagsOf));
}

/** AddLevel.FreeFloorSlots: how many floors «Добавить этаж», «Добавить подвал» or a copy may still add. */
export function freeFloorSlots(levels: readonly Level[], tagsOf: TagsOf = knownTags): number {
  const ordinary = levels.filter(level => !isRoofLevel(level, tagsOf)).length;
  return Math.max(0, Math.min(MAX_LEVELS - ordinary, MAX_LEVELS_WITH_ROOF - levels.length));
}

/** AddLevel.CopyOf: a floor added or copied leaves the roof rooms behind; a room whose removal is refused stays. */
export function withoutRoofRooms(level: Level, tagsOf: TagsOf = knownTags): Level {
  let result = level;
  for (let roof = result.rooms.find(room => isRoofRoom(result, room, tagsOf)); roof; roof = result.rooms.find(room => isRoofRoom(result, room, tagsOf))) {
    const removed = removeRoom(result, roof.index).level;
    if (!removed || removed.rooms.length >= result.rooms.length) break;
    result = removed;
  }
  return result;
}

/** AddLevel.UpperInsertIndex: a floor added above goes under the roof floors on top. */
export function upperInsertIndex(levels: readonly Level[], tagsOf: TagsOf = knownTags): number {
  let index = levels.length;
  while (index > 0 && isRoofLevel(levels[index - 1], tagsOf)) index--;
  return index;
}
